/* Copyright © 2026 YOUR GROWTH 365 (Jonas Brössel) — Proprietary. See LICENSE in repo root. */
/* =============================================================================
   CORE / CAMPAIGNS-DATA — mail_campaigns CRUD + Versand-RPCs (S227 CUT D)
   =============================================================================
   Datenschicht für Kampagnen. Zugriff über den RLS-gescopeten authenticated-
   Client (window.App.supabase); Server erzwingt Tenant + Rolle (phase_167).
   Modul-Cache speist die Liste synchron; Writes patchen ihn + melden Änderungen.

   Tenant-ID AUSSCHLIESSLICH über den zentralen Resolver
   window.App.getCurrentRestaurantId (kein eigener — S226-HF2-#3-Lehre).
   Versand/Preview/Test laufen über SECURITY-DEFINER-RPCs (Status-Guard,
   Audience zur Sendezeit, kein Cross-Tenant).
============================================================================ */

const TABLE = 'mail_campaigns';

let _campaigns = [];
let _loaded = false;
let _loadInFlight = null;
let _onChange = null;

function supa() {
  return (typeof window !== 'undefined' && window.App && window.App.supabase) || null;
}

/** Zentraler Tenant-Resolver (kein Dual-System). */
export function currentRestaurantId() {
  if (typeof window !== 'undefined' && window.App
      && typeof window.App.getCurrentRestaurantId === 'function') {
    return window.App.getCurrentRestaurantId() || null;
  }
  return null;
}

export function onCampaignsChanged(cb) { _onChange = typeof cb === 'function' ? cb : null; }
function _notify() { try { if (_onChange) _onChange(); } catch (_) { /* tolerant */ } }

export function getCampaignsCache() { return _campaigns.slice(); }
export function isCampaignsLoaded() { return _loaded; }
export function getCampaign(id) { return _campaigns.find(c => c.id === id) || null; }
export function _resetForTests() { _campaigns = []; _loaded = false; _loadInFlight = null; _onChange = null; }

/** Kampagnen des Tenants laden (RLS-gescopet), parallele Aufrufe dedupliziert. */
export async function loadCampaigns(force) {
  if (_loaded && !force) return _campaigns.slice();
  if (_loadInFlight) return _loadInFlight;
  const client = supa();
  if (!client) { _loaded = true; return []; }
  _loadInFlight = (async () => {
    try {
      const { data, error } = await client.from(TABLE)
        .select('*').order('created_at', { ascending: false });
      if (error) throw error;
      _campaigns = Array.isArray(data) ? data : [];
      _loaded = true;
      return _campaigns.slice();
    } catch (e) {
      console.warn('[loadCampaigns]', e && e.message);
      return _campaigns.slice();
    } finally { _loadInFlight = null; }
  })();
  return _loadInFlight;
}

/** Entwurf anlegen (create-on-accept-only). Liefert die Zeile. */
export async function createCampaign(c) {
  const client = supa();
  if (!client) throw new Error('Kein Supabase-Client');
  const row = {
    restaurant_id:   currentRestaurantId(),
    name:            c.name,
    status:          'draft',
    subject:         c.subject || '',
    body:            c.body || '',
    attachment:      c.attachment || null,
    audience_filter: c.audience_filter || {},
  };
  const { data, error } = await client.from(TABLE).insert(row).select().single();
  if (error) throw error;
  _campaigns.unshift(data);
  _notify();
  return data;
}

/** Entwurf patchen. */
export async function updateCampaign(id, patch) {
  const client = supa();
  if (!client) throw new Error('Kein Supabase-Client');
  const { data, error } = await client.from(TABLE).update(patch).eq('id', id).select().single();
  if (error) throw error;
  const i = _campaigns.findIndex(x => x.id === id);
  if (i >= 0) _campaigns[i] = data; else _campaigns.unshift(data);
  _notify();
  return data;
}

export async function deleteCampaign(id) {
  const client = supa();
  if (!client) throw new Error('Kein Supabase-Client');
  const { error } = await client.from(TABLE).delete().eq('id', id);
  if (error) throw error;
  _campaigns = _campaigns.filter(x => x.id !== id);
  _notify();
}

/** Empfänger-Vorschau (Anzahl + maskierte Stichprobe + unlinked-Hinweis).
 *  Reine Lese-RPC, tenant-geprüft; live bei jeder Filter-Änderung aufrufbar. */
export async function previewAudience(filter) {
  const client = supa();
  const rid = currentRestaurantId();
  if (!client || !rid) return { count: 0, sample: [], unlinked_excluded: 0 };
  const { data, error } = await client.rpc('gg_campaign_preview', { p_restaurant_id: rid, p_filter: filter || {} });
  if (error) throw error;
  return data || { count: 0, sample: [], unlinked_excluded: 0 };
}

/** Sofort-Versand (Status-Guard serverseitig). */
export async function startCampaignNow(id) {
  const client = supa();
  if (!client) throw new Error('Kein Supabase-Client');
  const { data, error } = await client.rpc('gg_campaign_start_now', { p_campaign_id: id });
  if (error) throw error;
  await loadCampaigns(true);
  _notify();
  return data;
}

/** Planen: Status scheduled + Zeitpunkt (RLS-UPDATE). */
export async function scheduleCampaign(id, whenIso) {
  return updateCampaign(id, { status: 'scheduled', scheduled_at: whenIso });
}

/** Storno einer geplanten Kampagne (race-sicher via Status-WHERE). */
export async function cancelCampaign(id) {
  const client = supa();
  if (!client) throw new Error('Kein Supabase-Client');
  const { data, error } = await client.from(TABLE)
    .update({ status: 'cancelled' }).eq('id', id).eq('status', 'scheduled').select();
  if (error) throw error;
  await loadCampaigns(true);
  _notify();
  return Array.isArray(data) && data.length > 0;   // false = war nicht mehr 'scheduled'
}

/** Test-Send an eine Adresse (genau 1 Zeile, kein Gate, kein Zähler). */
export async function testSendCampaign(id, email) {
  const client = supa();
  if (!client) throw new Error('Kein Supabase-Client');
  const { data, error } = await client.rpc('gg_campaign_test_send', { p_campaign_id: id, p_email: email });
  if (error) throw error;
  return data;
}
